import React, { useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Bot, User, Send, Sparkles, Headphones } from 'lucide-react';
import { api, API } from '../lib/api';
import { MicButton } from '../components/MicButton';
import { ListenButton } from '../components/ListenButton';

const SUGGESTIONS = [
  "I'm on ODSP in Toronto and use a wheelchair. What am I missing?",
  'How do I apply for the Disability Tax Credit?',
  'Can I work while receiving ODSP?',
  "What's the difference between CPP-D and ODSP?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([{ role: 'assistant', content: "Hi! I'm your AccessBenefits Companion. Ask me anything about Ontario and federal disability programs — I'll cite the official sources." }]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [handsFree, setHandsFree] = useState(() => localStorage.getItem('hands_free') === '1');
  const autoAudio = useRef(null);
  const scrollRef = useRef();

  const speak = async (text) => {
    try {
      autoAudio.current?.pause();
      const { data } = await api.post('/tts', { text, lang: localStorage.getItem('lang') || 'en' });
      const a = new Audio(`${API.replace(/\/api$/, '')}${data.url}`);
      autoAudio.current = a;
      await a.play();
    } catch { /* silent */ }
  };
  const toggleHandsFree = () => { const v = !handsFree; setHandsFree(v); localStorage.setItem('hands_free', v ? '1' : '0'); if (!v) autoAudio.current?.pause(); };

  const send = async (text, viaVoice = false) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'X-Lang': localStorage.getItem('lang') || 'en' },
        credentials: 'include',
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          if (!p.startsWith('data: ')) continue;
          const payload = p.slice(6);
          if (payload === '[DONE]') continue;
          if (payload.startsWith('[ERROR]')) {
            setMessages((m) => { const c = [...m]; c[c.length - 1].content += `\n\n_${payload}_`; return c; });
            continue;
          }
          setMessages((m) => { const c = [...m]; c[c.length - 1].content += payload; return c; });
          full += payload;
          setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 0);
        }
      }
      if ((viaVoice || handsFree) && full.trim()) speak(full);
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1].content = 'Sorry, something went wrong.'; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3" style={{fontFamily: 'Lexend'}}>
          <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><Bot className="w-5 h-5" /></div>
          AccessBenefits Companion
        </h1>
        <p className="text-muted-foreground mt-2">Ask plain-language questions. I only answer using our verified benefits database.</p>
        <button onClick={toggleHandsFree} role="switch" aria-checked={handsFree} data-testid="hands-free-toggle" className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${handsFree ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary'}`}>
          <Headphones className="w-4 h-4" /> Hands-free {handsFree ? 'on' : 'off'} <span className="text-xs opacity-80">— replies are read aloud{handsFree ? '' : ' (always on after a voice question)'}</span>
        </button>
      </div>

      <Card className="p-0 bg-card overflow-hidden">
        <div ref={scrollRef} className="p-5 sm:p-6 max-h-[60vh] overflow-y-auto space-y-5" data-testid="chat-scroll">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${m.role === 'user' ? 'bg-secondary text-foreground' : 'bg-primary text-primary-foreground'}`}>
                {m.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
              </div>
              <div className={`max-w-[80%] p-3 rounded-xl whitespace-pre-wrap text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`}>
                {m.content || <span className="opacity-50">…</span>}
                {m.role === 'assistant' && m.content && !(streaming && i === messages.length - 1) && <div className="mt-1 -mb-1 -ml-2"><ListenButton text={m.content} label="reply" testId={`listen-reply-${i}`} /></div>}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-4 bg-card">
          {messages.length <= 1 && (
            <div className="mb-3 flex gap-2 flex-wrap">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid={`suggest-${s.substring(0,8)}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-secondary flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-primary" /> {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about ODSP, DTC, ADP, housing…"
              className="resize-none min-h-[52px]"
              data-testid="chat-input"
            />
            <MicButton label={null} size="default" className="h-[52px] w-[52px] p-0" testId="chat-mic" onText={(txt) => send(txt, true)} />
            <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="chat-send" className="h-[52px] bg-primary hover:bg-primary/90 text-primary-foreground">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
